/* eslint-disable no-new */
import React, { Component, cloneElement } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { debounce } from 'dpl/shared/utils';
import { GOOGLE_MAP_IDS } from 'dpl/shared/constants/shared';

export function getMapBounds(googleMap) {
  const bounds = googleMap.getBounds();
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  return {
    ne: {
      lat: ne.lat(),
      lng: ne.lng()
    },
    sw: {
      lat: sw.lat(),
      lng: sw.lng()
    }
  };
}

export function getMapCenter(googleMap) {
  const center = googleMap.getCenter();

  return {
    lat: center.lat(),
    lng: center.lng()
  };
}

export default class GoogleMap extends Component {
  static propTypes = {
    id: PropTypes.string,
    centerOnChange: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    coordinates: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        lat: PropTypes.number.isRequired,
        lng: PropTypes.number.isRequired
      })
    ).isRequired,
    onMapLoad: PropTypes.func,
    onMapUpdate: PropTypes.func,
    onMapCreate: PropTypes.func,
    zoomable: PropTypes.bool,
    boundsPadding: PropTypes.number,
    // eslint-disable-next-line react/forbid-prop-types
    options: PropTypes.object,
    onClick: PropTypes.func
  };

  static defaultProps = {
    id: GOOGLE_MAP_IDS.UPDATED,
    centerOnChange: false,
    children: null,
    className: null,
    onMapLoad: null,
    onMapUpdate: null,
    onMapCreate: null,
    zoomable: false,
    boundsPadding: null,
    options: null,
    onClick: null
  };

  componentDidMount() {
    this.initMapIfNeeded();
  }

  componentDidUpdate() {
    this.initMapIfNeeded();
    this.resetCenterIfNeeded();
  }

  state = { hasMap: false };

  googleMap = null;

  el = null;

  computeGoogleCoordinates() {
    return this.props.coordinates.map(
      ({ lat, lng }) => new window.google.maps.LatLng(lat, lng)
    );
  }

  computeBounds() {
    return this.computeGoogleCoordinates().reduce((bounds, coord) => {
      bounds.extend(coord);
      return bounds;
    }, new window.google.maps.LatLngBounds());
  }

  initMapIfNeeded() {
    if (!this.googleMap && window.google?.maps) {
      this.initMap();
    }
  }

  initMap = () => {
    if (this.state.hasMap || !window.google?.maps) {
      return;
    }

    const {
      id,
      coordinates,
      onMapLoad,
      onMapUpdate,
      onMapCreate,
      zoomable,
      options,
      onClick
    } = this.props;

    // Create a map object and specify the DOM element for display.
    const bounds = this.computeBounds();
    const initParams = {
      mapId: id,
      center: bounds.getCenter(),
      zoom: 10,
      disableDefaultUI: true,
      disableDoubleClickZoom: !zoomable,
      zoomControl: zoomable,
      options
    };

    if (zoomable) {
      initParams.zoomControlOptions = {
        position: window.google.maps.ControlPosition.RIGHT_TOP
      };
    }

    this.googleMap = new window.google.maps.Map(this.el, initParams);

    if (onMapCreate) {
      onMapCreate(this.googleMap);
    }

    if (onMapUpdate) {
      this.googleMap.addListener('bounds_changed', this.handleMapUpdate);
    }

    if (onClick) {
      this.googleMap.addListener('click', onClick);
    }

    if (onMapLoad) {
      window.google.maps.event.addListenerOnce(
        this.googleMap,
        'tilesloaded',
        onMapLoad
      );
    }

    if (coordinates.length > 1) {
      this.resetCenterIfNeeded();
    }

    this.setState({ hasMap: true });
  };

  resetCenterIfNeeded() {
    if (!this.googleMap || !this.props.centerOnChange) {
      return;
    }

    const bounds = this.computeBounds();
    const zoomOpt = this.props.options?.zoom || null;

    window.google.maps.event.addListenerOnce(
      this.googleMap,
      'bounds_changed',
      () => {
        this.googleMap.setCenter(bounds.getCenter());
        const currentZoom = this.googleMap.getZoom();

        if (zoomOpt && currentZoom > zoomOpt) {
          this.googleMap.setZoom(zoomOpt);
        }
      }
    );
    this.googleMap.fitBounds(bounds, this.props.boundsPadding);
  }

  // README: I don't think this is actually used anymore,
  // but I'm keeping it here because of its dark magicks
  // from https://stackoverflow.com/a/3527136/3220101
  drawCircle = () => {
    const bounds = this.computeBounds();
    const center = bounds.getCenter();
    const ne = bounds.getNorthEast();

    // r = radius of the earth in statute miles
    const r = 3963.0;

    // Convert lat or lng from decimal degrees into radians (divide by 57.2958)
    const lat1 = center.lat() / 57.2958;
    const lon1 = center.lng() / 57.2958;
    const lat2 = ne.lat() / 57.2958;
    const lon2 = ne.lng() / 57.2958;

    // distance = circle radius from center to Northeast corner of bounds
    const dis =
      r *
      Math.acos(
        Math.sin(lat1) * Math.sin(lat2) +
          Math.cos(lat1) * Math.cos(lat2) * Math.cos(lon2 - lon1)
      );

    // default to a good sized circle when we have only one coordinate
    const circleRadius = dis * 1000 || 4000;

    new window.google.maps.Circle({
      map: this.googleMap,
      center,
      radius: circleRadius,
      strokeColor: '#2b36e0',
      strokeOpacity: 0.4,
      strokeWeight: 3,
      fillColor: '#2b36e0',
      fillOpacity: 0.35
    });
  };

  handleMapUpdate = debounce(() => {
    this.props.onMapUpdate(this.googleMap);
  }, 500);

  render() {
    return (
      <div className="w-100 h-100">
        <div
          className={classnames('w-100 h-100 GoogleMap', this.props.className)}
          ref={el => {
            this.el = el;
          }}
        >
          Loading Map ...
        </div>
        {this.googleMap && this.props.children && (
          <div className="dn">
            {React.Children.map(this.props.children, child =>
              child ? cloneElement(child, { map: this.googleMap }) : null
            )}
          </div>
        )}
      </div>
    );
  }
}
